import {formatMolecularFormula} from 'chemistry/molecules/MoleculeUtil';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  GetMolecularFormulaQuery as GetMolecularFormulaQueryResponse,
  GetMolecularFormulaQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetMolecularFormulaQuery';
import {GET_MOLECULAR_FORMULA_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {
  CustomResolutionQueryType,
  GetMolecularFormulaQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType, CustomEvalResult, TypedValue} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export async function bulkResolveGetMolecularFormulaToServerResolutionResult(
  queries: Array<GetMolecularFormulaQuery>
): Promise<Array<ServerResolutionResult>> {
  const moleculeIds = queries.map((query) => query.moleculeId);

  const queryResult = await doQueryWithoutCache<
    GetMolecularFormulaQueryResponse,
    GetMolecularFormulaQueryVariables
  >({query: GET_MOLECULAR_FORMULA_QUERY}, {variables: {moleculeIds}});

  // Build a map of moleculeId -> molecular formula for efficient lookup
  const molecularFormulaByMoleculeId = new Map<string, string | null>();
  for (const molecule of queryResult.molecules) {
    if (isInaccessible(molecule)) {
      molecularFormulaByMoleculeId.set(molecule.invalidId, null);
    } else {
      const molecularFormula = molecule.currentVersion.chemicalStructure.chemicalFormula;
      if (molecularFormula.__typename === 'ChemicalFormulaNode') {
        molecularFormulaByMoleculeId.set(molecule.id, formatMolecularFormula(molecularFormula.value));
      } else {
        molecularFormulaByMoleculeId.set(molecule.id, null);
      }
    }
  }

  // Return results in the same order as the input queries
  return queries.map((query) => {
    const molecularFormula = molecularFormulaByMoleculeId.get(query.moleculeId);
    if (molecularFormula == null) {
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve molecular formula');
    }
    return makeCustomResolutionResultFromTypedValue({
      type: FieldType.TEXT,
      value: molecularFormula,
    });
  });
}

export function getMolecularFormulaColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // The molecule ID comes from the first cell dependency (the Name column containing the bioentity)
  const bioentityTypedValue = cellDependencies[0];
  // If there's no bioentity in the dependency, return empty
  if (bioentityTypedValue == null || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
    return {val: typedValue, error: null, locked: false};
  }
  const moleculeId = bioentityTypedValue.bioentitySummary.id;
  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_MOLECULAR_FORMULA,
      moleculeId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
